/** ADM-22 Entrepôts · ADM-23 Fiche entrepôt et créneaux de retrait. */
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, SelectField, Table, useToast } from '@myron/design-system';
import { Plus, Trash2 } from 'lucide-react';
import { get, put } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { EntetePage, ErreurFormulaire, EtatRequete, StatutBadge } from '../../../shared/ui/composants';
import { JOURS, useEntrepots, type Entrepot } from './commun';

export function PageEntrepots() {
  const r = useEntrepots();
  return (
    <>
      <EntetePage titre="Entrepôts" sousTitre="Plusieurs entrepôts dès le lancement : chaque stock est rattaché à un entrepôt et à un lot." />
      <EtatRequete requete={r} vide={(d) => !d.length} titreVide="Aucun entrepôt dans votre portée" enfants={(d) => (
        <div className="my-grille">
          {d.map((e) => (
            <Card key={e.id} className="my-pile">
              <div className="my-ligne my-ligne--espace"><Badge ton="marque">{e.code}</Badge><StatutBadge statut={e.statut} /></div>
              <h2 className="my-titre-4"><Link to={`/admin/entrepots/${e.id}`}>{e.nom}</Link></h2>
              <p className="my-petit my-secondaire">{e.adresse.ligne1}, {e.adresse.codePostal} {e.adresse.ville}</p>
              <div className="my-ligne">
                <Badge ton={e.retraitActif ? 'succes' : 'neutre'}>{e.retraitActif ? 'Retrait actif' : 'Pas de retrait'}</Badge>
                <Badge ton={e.livraisonActive ? 'succes' : 'neutre'}>{e.livraisonActive ? 'Livraison active' : 'Pas de livraison'}</Badge>
              </div>
              <div className="my-ligne">
                <Link to={`/admin/stocks?entrepotId=${e.id}`}>Stocks</Link>
                <Link to={`/admin/preparation?entrepotId=${e.id}`}>Préparation</Link>
              </div>
            </Card>
          ))}
        </div>
      )} />
    </>
  );
}

type Creneau = Entrepot['creneaux'][number];

export function PageEntrepot() {
  const { entrepotId = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const r = useQuery({ queryKey: ['logistique', 'entrepot', entrepotId], queryFn: () => get<Entrepot>(`/admin/entrepots/${entrepotId}`) });
  const [creneaux, setCreneaux] = useState<Creneau[] | null>(null);
  const liste = creneaux ?? r.data?.creneaux ?? [];
  const maj = (i: number, champ: keyof Creneau, v: string) => setCreneaux(liste.map((c, k) => (k === i ? { ...c, [champ]: champ === 'jourSemaine' || champ === 'capacite' ? Number(v) : v } : c)));
  const m = useMutation({
    mutationFn: () => put<Creneau[]>(`/admin/entrepots/${entrepotId}/creneaux`, liste.map(({ jourSemaine, debut, fin, capacite }) => ({ jourSemaine, debut, fin, capacite })), { version: r.data!.version }),
    onSuccess: () => { setCreneaux(null); void qc.invalidateQueries({ queryKey: ['logistique'] }); toast({ ton: 'succes', titre: 'Créneaux enregistrés' }); },
  });
  const editable = peut('entrepot.gerer');
  return (
    <EtatRequete requete={r} enfants={(e) => (
      <>
        <EntetePage titre={e.nom} sousTitre={`${e.code} · ${e.adresse.ligne1}, ${e.adresse.codePostal} ${e.adresse.ville}`} fil={[{ libelle: 'Entrepôts', vers: '/admin/entrepots' }, { libelle: e.code }]} />
        <Card className="my-pile">
          <div className="my-ligne my-ligne--espace">
            <h2 className="my-titre-4">Créneaux de retrait</h2>
            {editable && e.retraitActif && <Button compact variante="secondaire" icone={Plus} onClick={() => setCreneaux([...liste, { id: `nouveau-${liste.length}`, jourSemaine: 1, debut: '08:00', fin: '12:00', capacite: 6 }])}>Ajouter un créneau</Button>}
          </div>
          {!e.retraitActif ? <p className="my-secondaire">Le retrait n’est pas proposé dans cet entrepôt.</p> : (
            <Table<Creneau> legende="Créneaux de retrait" lignes={liste} cle={(c) => c.id} colonnes={[
              { cle: 'j', titre: 'Jour', rendu: (c) => (editable ? <SelectField label={`Jour du créneau ${liste.indexOf(c) + 1}`} value={String(c.jourSemaine)} onChange={(ev) => maj(liste.indexOf(c), 'jourSemaine', ev.target.value)} options={JOURS.slice(1).map((j, k) => ({ value: String(k + 1), label: j }))} /> : JOURS[c.jourSemaine]) },
              { cle: 'd', titre: 'Début', rendu: (c) => (editable ? <input className="my-input" type="time" aria-label={`Début du créneau ${liste.indexOf(c) + 1}`} value={c.debut} onChange={(ev) => maj(liste.indexOf(c), 'debut', ev.target.value)} /> : c.debut) },
              { cle: 'f', titre: 'Fin', rendu: (c) => (editable ? <input className="my-input" type="time" aria-label={`Fin du créneau ${liste.indexOf(c) + 1}`} value={c.fin} onChange={(ev) => maj(liste.indexOf(c), 'fin', ev.target.value)} /> : c.fin) },
              { cle: 'c', titre: 'Capacité', num: true, rendu: (c) => (editable ? <input className="my-input" style={{ width: 80 }} type="number" min={1} aria-label={`Capacité du créneau ${liste.indexOf(c) + 1}`} value={c.capacite} onChange={(ev) => maj(liste.indexOf(c), 'capacite', ev.target.value)} /> : c.capacite) },
              ...(editable ? [{ cle: 'a', titre: 'Action', actions: true, rendu: (c: Creneau) => <Button compact variante="tertiaire" icone={Trash2} aria-label={`Supprimer le créneau ${liste.indexOf(c) + 1}`} onClick={() => setCreneaux(liste.filter((x) => x !== c))}>Supprimer</Button> }] : []),
            ]} />
          )}
          <ErreurFormulaire erreur={m.error} />
          {editable && creneaux && <div className="my-ligne"><Button chargement={m.isPending} onClick={() => m.mutate()}>Enregistrer les créneaux</Button><Button variante="tertiaire" onClick={() => setCreneaux(null)}>Annuler les modifications</Button></div>}
        </Card>
      </>
    )} />
  );
}
